import { z } from "zod/v4";

import {
  AgenticLogicCustomerAmountSpentDescriptions,
  AgenticLogicCustomerDataDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";

export const AgenticLogicCustomerAmountSpentSchema = z
  .object({
    amount: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicCustomerAmountSpentDescriptions.amount),
    currency: z
      .string()
      .describe(AgenticLogicCustomerAmountSpentDescriptions.currency),
  })
  .describe(AgenticLogicCustomerAmountSpentDescriptions._schema);

export type AgenticLogicCustomerAmountSpent = z.infer<
  typeof AgenticLogicCustomerAmountSpentSchema
>;

/**
 * PII-MINIMAL GUARANTEE: ids, counts, and money aggregates ONLY. No email,
 * name, phone, or address field may EVER be added here — a contract property
 * (locked by the PII drift-guard spec), not a data-availability accident.
 * Richer profile data arrives at rule runtime via sdk.getCustomer().
 */
export const AgenticLogicCustomerDataSchema = z
  .object({
    id: z.string().describe(AgenticLogicCustomerDataDescriptions.id),
    // NULLABLE on purpose: checkout supplies a count only for a verified buyer
    // whose count the app's PCD grant covers, and no other surface supplies
    // one. A required number left the producers no option but to fabricate a
    // 0 — which reads to a rule as "first-time buyer" and made every
    // repeat-customer branch dead code.
    ordersCount: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .describe(AgenticLogicCustomerDataDescriptions.ordersCount),
    amountSpent: AgenticLogicCustomerAmountSpentSchema.optional().describe(
      AgenticLogicCustomerDataDescriptions.amountSpent
    ),
  })
  .describe(AgenticLogicCustomerDataDescriptions._schema);

export type AgenticLogicCustomerData = z.infer<
  typeof AgenticLogicCustomerDataSchema
>;

assertDescriptions<
  typeof AgenticLogicCustomerAmountSpentDescriptions,
  AgenticLogicCustomerAmountSpent
>();
assertDescriptions<
  typeof AgenticLogicCustomerDataDescriptions,
  AgenticLogicCustomerData
>();
